import Image from 'next/image'

import { ReactComponent as ChevronRight } from 'public/assets/icons/chevron-right.svg'

interface Props {
  title: string
  list: StrapiSimpleCardComponent[]
}

type Metadata = {
  color: string
  ctaLink: string
  ctaTitle: string
  isFeatured: boolean
} | null

function CreatorMoreInfo({ title, list }: Props): JSX.Element {
  return (
    <section className="bg-gradient-to-b from-[#F8F9FA] to-white">
      <div className="container | font-body | py-10 md:py-18">
        <h2 className="font-heading text-3xl lg:text-5xl font-medium lg:text-center text-gray-90 | lg:max-w-col-10 lg:mr-auto lg:ml-auto mb-16">
          {title}
        </h2>

        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {list.map(item => {
            const _meta = item.meta as Metadata

            if (_meta?.isFeatured)
              return <FeaturedCard key={item.id} details={item} />
            else return <NormalCard key={item.id} details={item} />
          })}
        </div>
      </div>
    </section>
  )
}

function FeaturedCard({ details }: { details: StrapiSimpleCardComponent }) {
  const _meta = details.meta as Metadata
  return (
    <a
      href={_meta?.ctaLink}
      className="col-span-2 flex flex-col-reverse sm:flex-row justify-between | rounded-2xl shadow-lg"
      style={{ backgroundColor: _meta?.color ?? 'white' }}
    >
      <div className="flex flex-col | pt-8 pb-4 pl-8 pr-8">
        <h3 className="font-heading text-blue-web-90 text-2xl | mb-4">
          {details.title}
        </h3>
        <p className="text-sm text-gray-60 | mb-6">{details.description}</p>

        {_meta?.ctaTitle && (
          <div className="flex items-center gap-2 | mt-auto">
            <p className="text-sm font-medium text-blue-web-50">
              {_meta?.ctaTitle}
            </p>
            <ChevronRight className="text-blue-web-50" />
          </div>
        )}
      </div>

      <div>
        <Image
          src={details.image.data?.attributes.url ?? ''}
          alt={details.image.data?.attributes.alternativeText ?? ''}
          width={details.image.data?.attributes.width}
          height={details.image.data?.attributes.height}
          className="rounded-t-2xl md:rounded-tl-none md:rounded-r-2xl | h-full object-cover | mx-auto sm:ml-auto"
        />
      </div>
    </a>
  )
}

function NormalCard({ details }: { details: StrapiSimpleCardComponent }) {
  const _meta = details.meta as Metadata

  return (
    <a
      href={_meta?.ctaLink}
      className="col-span-2 sm:col-span-1 flex flex-col | rounded-2xl shadow-lg | p-8 pb-4"
    >
      <div className="flex items-start justify-between gap-8 | mb-4">
        <h3 className="font-heading text-blue-web-90 text-2xl  font-medium">
          {details.title}
        </h3>
        {details.icon.data?.attributes.url ? (
          <Image
            src={details.icon.data?.attributes.url}
            alt={details.icon.data?.attributes.alternativeText}
            width="40"
            height="40"
          />
        ) : null}
      </div>

      <p className="text-sm text-gray-60 | mb-6">{details.description}</p>

      {_meta?.ctaTitle && (
        <div className="flex items-center gap-2 | mt-auto">
          <p className="text-sm font-medium text-blue-web-50">
            {_meta?.ctaTitle}
          </p>
          <ChevronRight className="text-blue-web-50" />
        </div>
      )}
    </a>
  )
}

export default CreatorMoreInfo
